import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Table, Button, Input, Form, Typography, Tag, Tooltip, Select, Dropdown, Drawer, Segmented, Avatar, Badge } from 'antd';
import {
  PlusOutlined, DeleteOutlined, SearchOutlined,
  EyeOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
  UserSwitchOutlined, SubnodeOutlined
} from '@ant-design/icons';
import { FiCheckCircle, FiAlertTriangle, FiXCircle, FiUserCheck, FiSend, FiFlag } from 'react-icons/fi';
import type { MenuProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Paginations from '../../components/pagination';
import { categoryActions } from '../../actions/categoryActions';
import { userActions } from '../../actions/userActions';
import { commonActions } from '../../actions/commonActions';
import FilterPopover from '../../components/filter';
import ConfirmModal from '../../components/confirmModal';
import { enrollmentActions } from '../../actions/enrollmentActions';
import EnrollmentFormModal from '../../components/modals/enrollmentFormModal';
import TableData from '../../components/table';
import { axiosBase } from '../../services/axios';
import { useLocation, useNavigate } from 'react-router-dom';
import TextArea from 'antd/es/input/TextArea';
import GuardedButton from '../../components/guardedButton';
import { useCurrentPageAccess } from '../../services/helper';
import SpinnerLoader from '../../components/spinnerLoader';
import { getblobLink } from '../../services/helper';

const { Text } = Typography

type EnrollmentType = 'SERVICE' | 'TRAINING';

export interface FieldSpec {
  name: string;
  label: string;
  path?: string;
  type?: 'text' | 'email' | 'phone' | 'date' | 'textarea' | 'tags' | 'dropdown';
  editable?: boolean;
  rules?: any[];
  placeholder?: string;
  options?: Array<{ label: string; value: any; disabled?: boolean }>;
  formatter?: (value: any, item: any) => React.ReactNode;
  renderEdit?: (form: any, field: FieldSpec, item: any) => React.ReactNode;
}

interface enrollmentProps {
  role?: string;
}

interface QueryEnrollment {
  key: string;
  _id: string;
  studentId?: string;
  studentName: string;
  studentEmail: string;
  studentPhone: string;
  categoryName: string;
  serviceName: string;
  serviceType: string;
  gender?: string;
  price: number;
  balance?: number;
  paidAmount?: number;
  totalFee?: number;
  hasFeeRecord?: boolean;
  createdBy?: string;
  status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  documentStatus: string;
  pendingDocuments?: string[];
  applicationStatus?: string;
  applicationAssignedTo?: string;
  applicationAssignedToName?: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiEnrollment {
  _id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  studentPhone: string;
  categoryName: string;
  serviceName: string;
  serviceType: string;
  gender?: string;
  price: number;
  createdBy?: string;
  status: string;
  documentStatus: string;
  pendingDocuments?: string[];
  applicationStatus?: string;
  applicationAssignedTo?: string;
  applicationAssignedToName?: string;
  createdAt: string;
  updatedAt: string;
  balance?: number;
}

const Enrollment: React.FC<enrollmentProps> = ({ role }) => {

  const hasFetchedInitialData = useRef(false);
  const [searchText, setSearchText] = useState('');
  const [enrollmentType, setEnrollmentType] = useState<EnrollmentType>('SERVICE');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [filterData, setIsFilterData] = useState({ status: "", documentStatus: "", serviceType: "", studentId: "", categoryId: "", serviceId: "", isPendingDocument: "" });
  const pageAccess = useCurrentPageAccess();
  const [isFilterData, setIsFilter] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<any | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [mutableEnrollments, setMutableEnrollments] = useState<any[]>([]);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const { state } = useLocation();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isAssignDrawerOpen, setIsAssignDrawerOpen] = useState(false);
  const [assignRole, setAssignRole] = useState<string>('');
  const [assignUserId, setAssignUserId] = useState<string>('');
  const [assignEnrolmentIds, setAssignEnrolmentIds] = useState<string[]>([]);
  const [assignRemarks, setAssignRemarks] = useState<string>('');
  const [drawerUsers, setDrawerUsers] = useState<any[]>([]);
  const [drawerUsersLoading, setDrawerUsersLoading] = useState(false);
  const [tabCounts, setTabCounts] = useState<{ SERVICE?: number; TRAINING?: number }>({});
  const [lastCreatedType, setLastCreatedType] = useState<EnrollmentType | null>(null);
  const [countRefreshKey, setCountRefreshKey] = useState<number>(0);
  const assignApplicationState = useSelector((state: any) => state.assignApplicationReducer);
  const roleData = useSelector((state: any) => state.roleDataReducer);

  const defaultPayLoad = { page: 1, limit: 10, };
  const [visible, setVisible] = useState(false);
  const [limit, setLimit] = useState<number>(Number(defaultPayLoad.limit));
  const [currentPage, setCurrentPage] = useState<number>(1);

  const getEnrollment = useSelector((state: any) => state.getEnrollmentParamsReducer);
  const enrollmentData = useSelector((state: any) => state.createEnrollmentReducer);
  const deleteEnrollment = useSelector((state: any) => state.deleteEnrollmentReducer);
  const exportEnrollment = useSelector((state: any) => state.exportEnrollmentReducer);
  const categoryData = useSelector((state: any) => state.getCategoryReducer);
  const serviceData = useSelector((state: any) => state.getServiceReducer);
  const studentData = useSelector((state: any) => state.userListReducer);
  const roleListData = useSelector((state: any) => state.roleDataReducer);
  const enrollmentView = useSelector((state: any) => state.getEnrollmentViewReducer);
  const subscriptionStatus = useSelector((state: any) => state.userSubscriptionStatusReducer);
  const hasAccountingAccess = Boolean(subscriptionStatus?.data?.routes?.includes('/accounting'));


  

  // Build the filter payload (without pagination/type) so counts match the grid
  const countFilterPayload = useMemo(() => {
    const payload: any = {};
    if (debouncedSearchText?.trim()) {
      payload.searchText = debouncedSearchText.trim();
    }
    if (isFilterData) {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }
    return payload;
  }, [debouncedSearchText, isFilterData, filterData]);

  // Fetch counts for BOTH tabs whenever filters/search change or after actions
  useEffect(() => {
    let cancelled = false;

    const fetchCount = async (type: EnrollmentType) => {
      try {
        const res = await axiosBase.get('enrolment/list', {
          params: { ...countFilterPayload, enrolmentType: type, page: 1, limit: 1 },
        });
        const body = res?.data?.resultObject ?? res?.data;
        return body?.pagination?.total ?? 0;
      } catch (e) {
        return 0;
      }
    };

    (async () => {
      const [serviceTotal, trainingTotal] = await Promise.all([
        fetchCount('SERVICE'),
        fetchCount('TRAINING'),
      ]);
      if (cancelled) return;
      setTabCounts({ SERVICE: serviceTotal, TRAINING: trainingTotal });
    })();

    return () => { cancelled = true; };
  }, [countFilterPayload, countRefreshKey]);

  // Keep the active tab count in sync with the grid's own response
  useEffect(() => {
    const total = getEnrollment?.enrollmentdata?.pagination?.total;
    if (total === undefined) return;
    setTabCounts((prev) => ({ ...prev, [enrollmentType]: total }));
  }, [getEnrollment?.enrollmentdata, enrollmentType]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearchText(searchText.trim());
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [searchText]);


   useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
  
      const tab = params.get('tab')?.toString().toUpperCase();
      if (tab && ['SERVICE','TRAINING' ].includes(tab)) {
        (tab as any);
        setCurrentPage(1);
        setEnrollmentType(tab as EnrollmentType);
      }
    if (filterParam === 'pendingDocuments') {
      const newFilterData = { ...filterData, isPendingDocument: 'true' };
      setIsFilterData(newFilterData);
      setIsFilter(true);
      setEnrollmentType('TRAINING');
      setAppliedFilters({ isPendingDocument: 'true' });
    }


 
  }, [location.search, navigate, form]);



  useEffect(() => {
    const newStudentId = location.state?.newStudent;
    const categoryId = location.state?.category;
    const serviceId = location.state?.service;

    if (!newStudentId || !categoryId) return;

    setIsModalOpen(true);

    if (location.state && categoryData.isSuccess 
      && serviceData.isSuccess && studentData.isSuccess) {
      setTimeout(() => {
        form.setFieldsValue({
          studentId: newStudentId,
          categoryId,
          ...(serviceId ? { serviceId } : {}),
        });
      }, 200);
    }

  }, [location.state, categoryData, serviceData, studentData]);

  useEffect(() => {
    if (roleListData) {
      dispatch(userActions.userListRequest({ role: roleListData?.roleData?.find((r: any) => r.roleName === 'Student')?._id }));
    }
  }, [roleListData])

  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;

    dispatch(categoryActions.getCategory({ isActive: true }));
    dispatch(userActions.userRoleRequest());
    dispatch(commonActions.getService({ isActive: true }));
    dispatch(userActions.userRoleRequest());

  }, [dispatch, currentPage, limit]);


  useEffect(() => {
    if (enrollmentData && enrollmentData?.isSuccess) {
      setIsViewModalVisible(false);
      setIsEditMode(false);
      setIsModalOpen(false);
      setSelectedEnrollment(null);

      // Automatically switch to the tab of the newly created enrollment
      const targetType = lastCreatedType || enrollmentType;
      if (lastCreatedType && lastCreatedType !== enrollmentType) {
        setEnrollmentType(lastCreatedType);
      }
      setCurrentPage(1);
      setLastCreatedType(null);

      // ensure form is cleared after successful create
      try {
        form.resetFields();
      } catch (e) {
        // ignore if form not available
      }
      dispatch(enrollmentActions.getEnrollmentParams({ page: 1, limit, enrolmentType: targetType }));
      dispatch(enrollmentActions.resetCreateEnrollment());
    }
  }, [enrollmentData, dispatch, limit, lastCreatedType, enrollmentType]);

  useEffect(() => {
    if (assignApplicationState?.isSuccess) {
      setIsAssignDrawerOpen(false);
      setSelectedRowKeys([]);
      setAssignEnrolmentIds([]);
      setAssignUserId('');
      setAssignRole('');
      setDrawerUsers([]);
      setCountRefreshKey((k) => k + 1);

      const payload: any = {
        page: currentPage,
        limit,
        enrolmentType: enrollmentType,
        ...countFilterPayload,
      };
      dispatch(enrollmentActions.getEnrollmentParams(payload));
      dispatch(enrollmentActions.resetAssignApplication());
    }
  }, [assignApplicationState?.isSuccess, dispatch, currentPage, limit, enrollmentType, countFilterPayload]);

  useEffect(() => {
    const payload: any = {
      page: currentPage,
      limit,
      enrolmentType: enrollmentType,
    };
    if (debouncedSearchText?.trim()) {
      payload.searchText = debouncedSearchText.trim();
    }
    if (isFilterData) {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }
    dispatch(enrollmentActions.getEnrollmentParams(payload));
  }, [dispatch, currentPage, limit, debouncedSearchText, isFilterData, filterData, enrollmentType]);

  useEffect(() => {
    if (enrollmentView && enrollmentView.isSuccess) {
      const viewData = enrollmentView?.enrollmentview;
      if (viewData) {
        const transformedEnrollment = {
          _id: viewData.enrolmentId,
          studentName: `${viewData.student.firstName} ${viewData.student.lastName}`,
          studentEmail: viewData.student.email,
          categoryName: viewData.category.categoryName,
          serviceName: viewData.service.serviceName,
          servicePrice: viewData.service.price,
          startDate: viewData.startDate,
          price: viewData.price,
          status: viewData.status,
          categoryId: viewData.category._id,
          serviceId: viewData.service._id,
          studentId: viewData.student._id,
          noOfClasses: viewData.noOfClasses,
          documents: viewData.documents,
          attendanceSummary: viewData.attendanceSummary,
          applicationStatus: viewData.applicationStatus,
          applicationAssignedTo: viewData.applicationAssignedTo?._id,
          applicationAssignedToName: viewData.applicationAssignedTo
            ? `${viewData.applicationAssignedTo.firstName} ${viewData.applicationAssignedTo.lastName || ''}`.trim()
            : null,
          balance: viewData.balance,
        };
        setSelectedEnrollment(transformedEnrollment);
        setIsViewModalVisible(true);
        enrollmentView.isSuccess = false;
      }
    }
  }, [enrollmentView]);

  // Dedicated effect for delete success handling
  useEffect(() => {
    if (deleteEnrollment && deleteEnrollment.isSuccess) {
      setVisible(false);
      setRecordToDelete('');
      setSelectedEnrollment(null);
      setIsEditMode(false);
      setSelectedRowKeys((prev) => prev.filter((k) => k !== recordToDelete));
      setCountRefreshKey((k) => k + 1);

      // If last item on current page was deleted, move back 1 page if currentPage > 1
      const isLastItemOnPage = mutableEnrollments.length === 1 && currentPage > 1;
      const targetPage = isLastItemOnPage ? currentPage - 1 : currentPage;
      if (isLastItemOnPage) {
        setCurrentPage(targetPage);
      }

      const payload: any = {
        page: targetPage,
        limit,
        enrolmentType: enrollmentType,
        ...countFilterPayload,
      };
      dispatch(enrollmentActions.getEnrollmentParams(payload));
      dispatch(enrollmentActions.resetDeleteEnrollment());
    }
  }, [deleteEnrollment, dispatch, currentPage, limit, enrollmentType, countFilterPayload, mutableEnrollments.length, recordToDelete]);

  const apiEnrollments: ApiEnrollment[] = useMemo(() => {
    const data = getEnrollment?.enrollmentdata?.data;
    if (!data) return [];
    return Array.isArray(data) ? data : [];
  }, [getEnrollment?.enrollmentdata]);


  useEffect(() => {
    const mapped: QueryEnrollment[] = apiEnrollments.map((enrollment: ApiEnrollment) => ({
      key: enrollment._id,
      _id: enrollment._id,
      studentId: enrollment.studentId,
      studentName: enrollment.studentName,
      studentEmail: enrollment.studentEmail,
      studentPhone: enrollment.studentPhone,
      categoryName: enrollment.categoryName,
      serviceName: enrollment.serviceName,
      serviceType: enrollment.serviceType,
      price: enrollment.price,
      gender: enrollment.gender,
      balance: enrollment.balance,
      paidAmount: (enrollment as any).paidAmount,
      totalFee: (enrollment as any).totalFee,
      hasFeeRecord: (enrollment as any).hasFeeRecord,
      createdBy: enrollment.createdBy,
      status: enrollment.status as 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED',
      documentStatus: enrollment.documentStatus,
      pendingDocuments: enrollment.pendingDocuments || [],
      applicationStatus: enrollment.applicationStatus,
      applicationAssignedTo: enrollment.applicationAssignedTo,
      applicationAssignedToName: enrollment.applicationAssignedToName,
      createdAt: enrollment.createdAt,
      updatedAt: enrollment.updatedAt,
    }));

    setMutableEnrollments(mapped);
  }, [apiEnrollments]);

  const showModal = (enrollment: any) => {
    navigate(`/enrollments/view`, { state: { enrollmentId: enrollment.key, serviceType: enrollmentType || "" } });
  };


  const handleOnPageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setLimit(newPageSize);
  };

  const onFinish = (values: any) => {
    const allServices = serviceData?.servicedata?.services || [];
    const selectedService = allServices.find((s: any) => s._id === values.serviceId);
    const svcType = (selectedService?.serviceType || selectedService?.type || '').toUpperCase();
    const targetType: EnrollmentType = svcType === 'TRAINING' ? 'TRAINING' : 'SERVICE';
    setLastCreatedType(targetType);

    dispatch(enrollmentActions.createEnrollment(values));
  };

  const handleApplyFilter = (values: any) => {
    setIsFilter(true);
    setCurrentPage(1);

    const newFilterData: any = {
      status: "",
      documentStatus: "",
      serviceType: "",
      studentId: "",
      categoryId: "",
      serviceId: "",
      isPendingDocument: ""
    };
    const appliedFiltersTemp: any = {};

    if (values?.studentId) {
      newFilterData.studentId = values.studentId;
      appliedFiltersTemp.studentId = values.studentId;
    }
    if (values?.categoryId) {
      newFilterData.categoryId = values.categoryId;
      appliedFiltersTemp.categoryId = values.categoryId;
    }
    if (values?.serviceId) {
      newFilterData.serviceId = values.serviceId;
      appliedFiltersTemp.serviceId = values.serviceId;
    }
    if (values?.status) {
      newFilterData.status = values.status;
      appliedFiltersTemp.status = values.status;
    }
    if (values?.documentStatus) {
      newFilterData.documentStatus = values.documentStatus;
      appliedFiltersTemp.documentStatus = values.documentStatus;
    }
    if (values?.serviceType) {
      newFilterData.serviceType = values.serviceType;
      appliedFiltersTemp.serviceType = values.serviceType;
    }
    if (values?.isPendingDocument) {
      const pendingDocValue = Array.isArray(values.isPendingDocument)
        ? values.isPendingDocument[0]
        : values.isPendingDocument;
      newFilterData.isPendingDocument = pendingDocValue;
      appliedFiltersTemp.isPendingDocument = pendingDocValue;
    }
    setIsFilterData(newFilterData);
    setAppliedFilters(appliedFiltersTemp);
  };

  const handleEditSave = (values: any) => {

    const documentsArray = values.documents.map((doc: any) => ({
      label: doc.label,
      status: doc.status,
      required: doc.required ?? false,
    }));

    const payload = {
      _id: selectedEnrollment?._id,
      price: values.price,
      status: values.status,
      categoryId: values.categoryId || selectedEnrollment?.categoryId,
      studentId: values.studentId || selectedEnrollment?.studentId,
      serviceId: values.serviceId || selectedEnrollment?.serviceId,
      startDate: values.startDate,
      documents: documentsArray,
      applicationStatus: values.applicationStatus,
      applicationAssignedTo: selectedEnrollment?.applicationAssignedTo,
    };

    // include noOfClasses only for TRAINING services
    try {
      const allServices = serviceData?.servicedata?.services || [];
      const selServiceId = payload.serviceId;
      const selService = allServices.find((s: any) => s._id === selServiceId);
      const svcType = selService?.serviceType || selService?.type;
      if (selService && svcType === 'TRAINING') {
        (payload as any).noOfClasses = values.noOfClasses;
      }
    } catch (e) {
      // ignore
    }

    dispatch(enrollmentActions.createEnrollment(payload));
    setIsEditMode(false);

  };

  const handleClearFilter = () => {
    setCurrentPage(1);
    setIsFilter(false);
    setIsFilterData({ status: "", documentStatus: "", serviceType: "", studentId: "", categoryId: "", serviceId: "", isPendingDocument: "" });
    setAppliedFilters({});
  };

  const handleDelete = () => {
    dispatch(enrollmentActions.deleteEnrollment(recordToDelete));
  }

  const handleExport = (format: 'pdf' | 'excel') => {
    if (!mutableEnrollments || mutableEnrollments.length === 0) return;
    const payload: any = {
      format,
    };

    if (filterData && typeof filterData === 'object') {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }
    if (searchText && searchText.trim()) {
      payload.searchText = searchText.trim();
    }
    dispatch(enrollmentActions.exportEnrollment(payload));
  };

  // Assign application handlers
  const handleOpenAssignDrawer = () => {
    const validKeys = (selectedRowKeys as string[]).filter(key => {
      const enr = mutableEnrollments.find(e => e._id === key);
      return enr?.status !== 'CANCELLED';
    });
    if (validKeys.length === 0) return;
    setAssignEnrolmentIds(validKeys);
    setIsAssignDrawerOpen(true);
  };

  const handleRoleChange = async (roleId: string) => {
    setAssignRole(roleId);
    setAssignUserId('');
    setDrawerUsers([]);
    setDrawerUsersLoading(true);
    try {
      const response = await axiosBase.get('users/list', { params: { role: roleId } });
      const users = response?.data?.resultObject?.users || [];
      setDrawerUsers(users);
    } catch (e) {
      setDrawerUsers([]);
    }
    setDrawerUsersLoading(false);
  };

  const handleRemoveEnrolmentFromAssign = (id: string) => {
    const updated = assignEnrolmentIds.filter(eid => eid !== id);
    setAssignEnrolmentIds(updated);
    setSelectedRowKeys(updated);
    if (updated.length === 0) {
      setIsAssignDrawerOpen(false);
    }
  };

  const handleAssignSave = () => {
    if (!assignUserId || assignEnrolmentIds.length === 0) return;
    dispatch(enrollmentActions.assignApplication({
      enrolmentIds: assignEnrolmentIds,
      userId: assignUserId,
      remarks: assignRemarks,
    }));
  };

  const assignRoleOptions = useMemo(() => {
    const roles = roleData?.roleData?.roles || roleData?.roleData || [];
    if (!Array.isArray(roles)) return [];
    return roles
      .filter((r: any) => ['ADMIN', 'STAFF', 'INSTRUCTOR'].includes(r.role))
      .map((r: any) => ({ label: r.role.charAt(0) + r.role.slice(1).toLowerCase(), value: r._id }));
  }, [roleData]);

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: QueryEnrollment) => ({
      disabled: record.status === 'CANCELLED',
    }),
  };

  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'pdf',
      label: 'Export as PDF',
      icon: <FilePdfOutlined />,
      onClick: () => handleExport('pdf'),
    },
    {
      key: 'excel',
      label: 'Export as Excel',
      icon: <FileExcelOutlined />,
      onClick: () => handleExport('excel'),
    },
  ];


  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: 'Student Name', dataIndex: 'studentName', key: 'studentName',
        render: (text: string, record: QueryEnrollment) => {
          return (
            <div className="enrollment-contact-info">
              <Avatar size={36} src={record.gender === 'Female' ? `${getblobLink()}/public/asset/female-avatar.png` : `${getblobLink()}/public/asset/male-avatar.png`} style={{ backgroundColor: '#f0f2f5', objectFit: 'contain' }} />
              <div className="enrollment-student-details">
                <span className="enrollment-student-name">{text}</span>
                <Text type="secondary">{record.studentEmail}</Text>
              </div>
            </div>
          );
        },
      },
      {
        title: 'Service / Category', key: 'service',
        render: (_: any, record: QueryEnrollment) => (
          <div className="enrollment-service-info">
            <span className="enrollment-service-name">{record.serviceName}</span>
            <div className="enrollment-category-child">
              <SubnodeOutlined className="enrollment-category-child-icon" />
              <span className="enrollment-category-child-text">{record.categoryName}</span>
            </div>
          </div>
        ),
      },
    ];

    if (hasAccountingAccess) {
      cols.push({
        title: 'Payment',
        key: 'payment',
        width: 150,
        render: (_: any, record: QueryEnrollment) => {
          const total = record.totalFee ?? record.price ?? 0;

          // Prefer explicit paidAmount; fall back to price - balance only when a fee record exists
          const paid =
            record.paidAmount != null
              ? record.paidAmount
              : record.hasFeeRecord
                ? Math.max(total - (record.balance ?? 0), 0)
                : 0;

          const due = Math.max(total - paid, 0);
          const paidPct = total > 0 ? Math.min(Math.round((paid / total) * 100), 100) : 0;

          const state: 'unpaid' | 'partial' | 'paid' =
            paid <= 0 ? 'unpaid' : due <= 0 ? 'paid' : 'partial';


          return (
            <div className="enr-payment-col">
              <div className="enr-payment-top">
                <span className="enr-payment-price">₹ {total.toLocaleString()}</span>
                <span className={`enr-payment-pct enr-payment-pct--${state}`}>
                  {state === 'unpaid' ? 'Unpaid' : `${paidPct}%`}
                </span>
              </div>
              <div className="enr-payment-bar-track">
                <div
                  className={`enr-payment-bar-fill enr-payment-bar-fill--${state}`}
                  style={{ width: `${paidPct}%` }}
                />
              </div>
              {state === 'partial' && (
                <div className="enr-payment-balance">₹{due.toLocaleString()} due</div>
              )}
              {state === 'unpaid' && (
                <div className="enr-payment-balance">No payment recorded</div>
              )}
            </div>
          );
        },
      });
    }

    cols.push(
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        align: 'center' as const,
        render: (status: string) => {
          const statusMap: Record<string, { color: string; label: string }> = {
            ASSIGNED: { color: 'default', label: 'Assigned' },
            IN_PROGRESS: { color: 'processing', label: 'In Progress' },
            COMPLETED: { color: 'success', label: 'Completed' },
            CANCELLED: { color: 'error', label: 'Cancelled' },
          };

          const statusInfo = statusMap[status] || {
            color: 'default',
            label: status,
          };

          const color = status === 'IN_PROGRESS' ? 'cyan' : status === 'ASSIGNED' ? 'blue' :
            status === 'COMPLETED' ? 'green' : status === 'CANCELLED' ? 'red' : 'default';

          return <Tag style={{ border: "1px solid" }} color={color}>{statusInfo.label}</Tag>;
        },
      },
      {
        title: 'Application',
        key: 'application',
        align: 'center' as const,
        render: (_: any, record: QueryEnrollment) => {
          const appStatusMap: Record<string, { color: string; label: string; icon: React.ReactNode; tip: string }> = {
            ASSIGNED: { color: 'blue', label: 'Assigned', icon: <FiUserCheck size={13} />, tip: 'Assigned to a team member — not yet submitted' },
            SUBMITTED: { color: 'orange', label: 'Submitted', icon: <FiSend size={13} />, tip: 'Application submitted and awaiting review' },
            APPROVED: { color: 'green', label: 'Approved', icon: <FiCheckCircle size={13} />, tip: 'Application approved' },
            REJECTED: { color: 'red', label: 'Rejected', icon: <FiXCircle size={13} />, tip: 'Application rejected' },
            COMPLETED: { color: 'cyan', label: 'Completed', icon: <FiFlag size={13} />, tip: 'Application process completed' },
          };
          const info = record.applicationStatus ? appStatusMap[record.applicationStatus] : undefined;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              {record.applicationStatus ? (
                <Tooltip title={info?.tip ?? record.applicationStatus}>
                  <Tag className='tag-application-enrollment' style={{ border: '1px solid', padding: '5px' }} color={info?.color ?? 'default'}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, }}>
                      {info?.icon}
                      {info?.label ?? record.applicationStatus}
                    </span>
                  </Tag>
                </Tooltip>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>Not Assigned</Text>
              )}
              {record.applicationAssignedToName && (
                <Text style={{ fontSize: 12 }}>
                  {record.applicationAssignedToName}
                </Text>
              )}
            </div>
          );
        },
      },
      {
        title: 'Created',
        key: 'created',
        width: 180,
        align: 'center' as const,
        render: (_: any, record: QueryEnrollment) => {
          let dateStr = '—';
          if (record.createdAt) {
            try {
              const d = new Date(record.createdAt);
              const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
              const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
              dateStr = `${date}, ${time}`;
            } catch (e) {
              dateStr = record.createdAt;
            }
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Text style={{ fontSize: 13 }}>{dateStr}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>by {record.createdBy ?? '—'}</Text>
            </div>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        align: 'center' as const,
        render: (_: any, record: QueryEnrollment) => (
          <div className="enrollment-actions">
            {pageAccess.isView && <Tooltip title="View">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={(e) => { e.stopPropagation(); showModal(record); }}
              />
            </Tooltip>}
            {role === 'ADMIN' && <Tooltip title="Delete">
              <GuardedButton
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => { e.stopPropagation(); setVisible(true); setRecordToDelete(record.key); }}
              />
            </Tooltip>}
            {role != 'ADMIN' && !pageAccess.isView && <p className="text-small text-gray">No actions available</p>}
          </div>
        ),
      }
    );

    return cols;
  }, [hasAccountingAccess, pageAccess.isView, role]);

  const filterFields = useMemo(() => {
    const studentOptions = studentData?.userList?.users?.map((student: any) => ({
      label: `${student.firstName} ${student.lastName}`,
      value: student._id,
    })) || [];
    const categoryOptions = categoryData?.categorydata?.categories?.map((category: any) => ({
      label: category.categoryName,
      value: category._id,
    })) || [];
    const serviceOptions = serviceData?.servicedata?.services?.map((service: any) => ({
      label: `${service.serviceName}`,
      value: service._id,
    })) || [];

    return [
      {
        name: 'studentId',
        label: 'Student',
        type: 'select',
        placeholder: 'Select student',
        options: studentOptions,
        showSearch: true,
      },
      {
        name: 'categoryId',
        label: 'Category',
        type: 'select',
        placeholder: 'Select category',
        options: categoryOptions,
        showSearch: true,
      },
      {
        name: 'serviceId',
        label: 'Service',
        type: 'select',
        placeholder: 'Select service',
        options: serviceOptions,
        showSearch: true,
      },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Select status',
        options: [
          { label: 'Assigned', value: 'ASSIGNED' },
          { label: 'In Progress', value: 'IN_PROGRESS' },
          { label: 'Completed', value: 'COMPLETED' },
          { label: 'Cancelled', value: 'CANCELLED' },
        ],
        showSearch: false,
      },
      {
        name: 'isPendingDocument',
        label: 'Document Status',
        type: 'checkbox',
        placeholder: 'Select document status',
        options: [
          { label: 'Pending', value: 'true' },
          { label: 'All', value: 'false' },
        ],
        showSearch: false,
      }
    ];
  }, [studentData, categoryData, serviceData]);

  if (getEnrollment.isLoading) {
    return <SpinnerLoader />
  }

  

  return (
    <>
      <div className="enrollment-table-wrapper">
        <div className="enrollment-toolbar">
          <Input
            placeholder="Search by name or description"
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 350 }}
            className="enrollment-search-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <FilterPopover
            appliedCount={Object.keys(appliedFilters).length}
            fields={filterFields}
            onApply={handleApplyFilter}
            onClear={handleClearFilter}
            columns={2}
            placement="bottomLeft"
            initialValues={appliedFilters}
          />

          <div className="enrollment-search-actions">
            <Segmented
              value={enrollmentType}
              onChange={(val) => {
                setEnrollmentType(val as EnrollmentType);
                setCurrentPage(1);
              }}
              options={[
                {
                  label: (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                      <span>Service</span>
                      {tabCounts.SERVICE !== undefined && (
                        <Badge
                          count={tabCounts.SERVICE}
                          overflowCount={999}
                          showZero
                          style={{
                            backgroundColor: enrollmentType === 'SERVICE' ? '#1677ff' : '#c7c2c2',
                            color: enrollmentType === 'SERVICE' ? '#fff' : '#666',
                            fontSize: 11,
                            height: 18,
                            lineHeight: '18px',
                            minWidth: 18,
                            padding: '0 5px',
                            boxShadow: 'none',
                          }}
                        />
                      )}
                    </div>
                  ),
                  value: 'SERVICE',
                },
                {
                  label: (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                      <span>Training</span>
                      {tabCounts.TRAINING !== undefined && (
                        <Badge
                          count={tabCounts.TRAINING}
                          overflowCount={999}
                          showZero
                          style={{
                            backgroundColor: enrollmentType === 'TRAINING' ? '#1677ff' : '#c7c2c2',
                            color: enrollmentType === 'TRAINING' ? '#fff' : '#666',
                            fontSize: 11,
                            height: 18,
                            lineHeight: '18px',
                            minWidth: 18,
                            padding: '0 5px',
                            boxShadow: 'none',
                          }}
                        />
                      )}
                    </div>
                  ),
                  value: 'TRAINING',
                },
              ]}
            />
          </div>
          {selectedRowKeys.length > 0 && (
            <Button
              className="enrollment-action-btn"
              type="default"
              icon={<UserSwitchOutlined />}
              onClick={handleOpenAssignDrawer}
            >
              Assign ({selectedRowKeys.length})
            </Button>
          )}
          <Dropdown
            menu={{ items: exportMenuItems }}
            placement="bottomRight"
            disabled={!mutableEnrollments || mutableEnrollments.length === 0}
          >
            <Button
              className="enrollment-action-btn"
              icon={<DownloadOutlined />}
              loading={exportEnrollment?.isLoading}
              disabled={!mutableEnrollments || mutableEnrollments.length === 0}
            >
              Export
            </Button>
          </Dropdown>
          {pageAccess.isAdd &&
            <GuardedButton className="enrollment-action-btn"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalOpen(true)}
            >
              Add Enrollment
            </GuardedButton>}
        </div>

        <div className='driveMynt-card'>
          <TableData columns={columns} className='enrollment-table' dataSource={mutableEnrollments}
            pagination={false} rowKey={(record) => record.key} rowSelection={rowSelection}
            onRow={(record: any) => ({ onDoubleClick: () => { if (!pageAccess.isView) return; showModal(record); }, style: { cursor: 'pointer' } })} />
          <Paginations total={getEnrollment?.enrollmentdata?.pagination?.total} defaultCurrent={currentPage}
            defaultPageSize={limit} callback={handleOnPageChange} />
        </div>
        <EnrollmentFormModal
          open={isModalOpen}
          title='Enrollment Details'
          onCancel={() => { setIsModalOpen(false); form.resetFields(); }}
          onFinish={onFinish}
          form={form}
          isLoading={Boolean(enrollmentData?.isLoading)}  // ← add
        />
        <ConfirmModal centered
          content="Are you sure you want to delete this enrollment?"
          visible={visible}
          cancelText='cancel'
          okText='ok'
          title='Confirm Delete'
          onOk={handleDelete}
          onCancel={() => setVisible(false)}
        />

        <Drawer
          className="assign-drawer"
          title={
            <div className="assign-drawer-title">
              <span className="assign-drawer-title-ic"><UserSwitchOutlined /></span>
              <div>
                <div className="assign-drawer-title-main">Assign Application</div>
                <div className="assign-drawer-title-sub">Route selected enrolments to a team member</div>
              </div>
            </div>
          }
          placement="right"
          width={480}
          open={isAssignDrawerOpen}
          onClose={() => {
            setIsAssignDrawerOpen(false);
            setAssignRole('');
            setAssignUserId('');
            setAssignRemarks('');
            setDrawerUsers([]);
          }}
          extra={
            <Button
              type="primary"
              icon={<UserSwitchOutlined />}
              disabled={!assignUserId || assignEnrolmentIds.length === 0}
              loading={assignApplicationState?.isLoading}
              onClick={handleAssignSave}
            >
              Assign
            </Button>
          }
        >
          {/* Context banner — explains what this drawer does */}
          <div className="assign-intro">
            <FiSend className="assign-intro-ic" />
            <span>
              Pick a <strong>role</strong>, choose the <strong>one user</strong> who will handle the
              selected enrolment{assignEnrolmentIds.length > 1 ? 's' : ''}, then optionally add a note before assigning.
            </span>
          </div>

          {/* Step 1 — Selected enrolments */}
          <section className="assign-step">
            <div className="assign-step-head">
              <div className="assign-step-titles">
                <div className="assign-step-title">Selected Enrolments</div>
                <div className="assign-step-sub">
                  {assignEnrolmentIds.length} application{assignEnrolmentIds.length > 1 ? 's' : ''} will be assigned
                </div>
              </div>
            </div>
            <div className="assign-enrol-list">
              {assignEnrolmentIds.map(eid => {
                const enrolment = mutableEnrollments.find(e => e._id === eid);
                const name = enrolment?.studentName || eid;
                return (
                  <div className="assign-enrol-card" key={eid}>
                    <span className="assign-enrol-avatar">{(name || '?').charAt(0)}</span>
                    <div className="assign-enrol-info">
                      <div className="assign-enrol-name">{name}</div>
                      <div className="assign-enrol-meta">
                        {enrolment?.serviceName && <span className="assign-enrol-service">{enrolment.serviceName}</span>}
                        {enrolment?.serviceType && <span className="assign-chip">{String(enrolment.serviceType).toLowerCase()}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="assign-enrol-remove"
                      aria-label="Remove enrolment"
                      onClick={() => handleRemoveEnrolmentFromAssign(eid)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Step 2 — Role */}
          <section className="assign-step">
            <div className="assign-step-head">
              <div className="assign-step-titles">
                <div className="assign-step-title">Select Role</div>
                <div className="assign-step-sub">Filter available users by their role</div>
              </div>
            </div>
            <Select
              placeholder="Select a role"
              style={{ width: '100%' }}
              size="large"
              value={assignRole || undefined}
              onChange={handleRoleChange}
              options={assignRoleOptions}
              allowClear
            />
          </section>

          {/* Step 3 — User  +  Step 4 — Remarks (shown once a role is chosen) */}
          {assignRole && (
            <>
              <section className="assign-step">
                <div className="assign-step-head">
                  <div className="assign-step-titles">
                    <div className="assign-step-title">Assign To</div>
                    <div className="assign-step-sub">
                      Choose one {(assignRoleOptions.find((o: any) => o.value === assignRole)?.label || 'user').toLowerCase()} to handle these applications
                    </div>
                  </div>
                </div>

                {drawerUsersLoading ? (
                  <div className="assign-empty">
                    <FiUserCheck style={{ fontSize: 18, color: '#c2c8d2' }} />
                    <span>Loading users…</span>
                  </div>
                ) : drawerUsers.length === 0 ? (
                  <div className="assign-empty">
                    <FiAlertTriangle style={{ fontSize: 18, color: '#c2c8d2' }} />
                    <span>No users found for this role</span>
                  </div>
                ) : (
                  <div className="assign-user-list">
                    {drawerUsers.map((user: any) => {
                      const selected = assignUserId === user._id;
                      const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}` || '?';
                      return (
                        <button
                          type="button"
                          key={user._id}
                          className={`assign-user-card${selected ? ' is-selected' : ''}`}
                          onClick={() => setAssignUserId(user._id)}
                        >
                          <span className="assign-radio" />
                          <span className="assign-user-avatar">{initials}</span>
                          <div className="assign-user-info">
                            <div className="assign-user-name">{user.firstName} {user.lastName || ''}</div>
                            <div className="assign-user-email">{user.email}</div>
                          </div>
                          {user.assignedEnrolments?.length > 0 && (
                            <span className="assign-user-count">{user.assignedEnrolments.length} assigned</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="assign-step">
                <div className="assign-step-head">
                  <div className="assign-step-titles">
                    <div className="assign-step-title">Remarks</div>
                    <div className="assign-step-sub">Optional note for this assignment</div>
                  </div>
                </div>
                <TextArea
                  rows={3}
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  placeholder="Enter remarks (optional)"
                  maxLength={500}
                  showCount
                  value={assignRemarks}
                  onChange={(e) => setAssignRemarks(e.target.value)}
                />
              </section>
            </>
          )}
        </Drawer>
      </div>
    </>
  );
};
export default Enrollment;
